import * as React from "react"
import styled from "styled-components"
import { ReactCompareSlider, styleFitContainer } from "react-compare-slider";
import parse from "html-react-parser"

import Layout from "../components/layout"
import Seo from "../components/seo"
import OutlineButton from "../components/buttons/outlineButton";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { ComparisonCharts } from "../components/sections/comparisonCharts";

const HeroSection = styled.section`
  margin-top: -66px;
  padding-top: 66px;
  background-color: #fff;
  background-image: url("../images/reset/reset-single-hero-bg.png");
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top center;
`

const HeroContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 1rem 0;
  margin: 0 auto;
  max-width: 1060px;
  font-size: clamp(1.25rem, 0.6985rem + 1.4706vw, 1.875rem);
  text-align: center;

  h1 {
    font-size: clamp(2rem, -0.6471rem + 7.0588vw, 5rem);
    text-align: center;
  }
`

const HeroCompare = styled.div`
  display: flex;
  gap: 50px;

  @media (max-width: 756px) {
    flex-direction: column;
  }
`

const CompareSection = styled.section`
  margin-top: -160px;
  padding-top: 240px;
  padding-bottom: 100px;
  background-color: #CCD6ED;
`;

const CompareInner = styled.div`
  display: flex;
  margin: 0 auto;
  max-width: 1060px;
  padding: 0 1rem;
  align-items: center;
  gap: 120px;

  @media (max-width: 756px) {
    flex-direction: column;
    gap: 60px;
  }

  div:first-child {
    flex: 1 66%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    font-size: clamp(1rem, 0.7243rem + 0.7353vw, 1.3125rem);
    line-height: 1.5;
    color: #4E4F51;

    h2 {
      font-size: clamp(1.5rem, 1.0037rem + 1.3235vw, 2.0625rem);
      color: #222a3f;
      margin-bottom: 0;
    }
  }

  div:last-child {
    flex: 1 33%;
  }

  .video-intro {
    margin-top: 2rem;
    margin-bottom: 0;
    font-size: 1.6rem;
  }
`

const YouTubeWrapper = styled.div`
  position: relative;
  min-width: 287px;
  max-width: 100%;

  &:before {
    content: '';
    display: block;
    padding-top: calc(16 / 9 * 100%);
  }

  > * {
    height: 100%;
    width: 100%;
    margin-bottom: 0 !important;
    position: absolute;
    top: 0;
    left: 0;
  }

  .gatsby-image-wrapper {
    aspect-ratio: 16 / 9;
  }

  button {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    webkit-appearance: none;
    background-color: rgba(0, 0, 0, 0.5);
    border: none;
    z-index: 2;
    opacity: 0;
    cursor: pointer;
    transition: opacity 0.2s ease-in-out;


    &:hover, &:focus {
      opacity: 1;
    }

    @media (max-width: 756px) {
      opacity: 1;
      background-color: rgba(0, 0, 0, 0.25);
    }

    span {
      background-image: url("../images/icons/play.svg");
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
      z-index: 3;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: inline-block;
      height: 3rem;
      width: 3rem;
    }
  }
`

const GridSection = styled.section`
  padding: 100px 0;
  background-color: #fafafa;
`

const GridInner = styled.div`
  max-width: 1060px;
  margin: 0 auto;
  padding: 0 1rem;
  text-align: center;
  font-weight: 700;

  h2 {
    font-size: clamp(1.5rem, 1.0037rem + 1.3235vw, 2.0625rem);
    color: #6C7E99;
  }
`

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 48px;
  margin-bottom: 35px;

  > div {
    flex: 1 33%;
    max-width: calc(33% - 35px);
    background-color: #fff;
    box-shadow: 0px 0px 20px 0px #E0E0E080;
    padding: 35px;
    display: flex;
    gap: 35px;
    align-items: center;
    text-align: left;
    font-weight: 400;

    @media (max-width: 960px) {
      flex: 1 50%;
      max-width: calc(50% - 35px);
    }

    @media (max-width: 680px) {
      flex: 1 100%;
      max-width: 100%;
    }
  }
`

const ColumnSection = styled.section`
  padding: 0 0 100px;
  background-color: #fafafa;
`

const ColumnInner = styled.div`
  max-width: 1060px;
  margin: 0 auto;
  padding: 0 1rem;

  h2 {
    font-size: clamp(1.5rem, 1.0037rem + 1.3235vw, 2.0625rem);
    color: #6C7E99;
  }
`

const Columns = styled.div`
  display: flex;
  gap: 70px;
  font-size: clamp(1rem, 0.7243rem + 0.7353vw, 1.3125rem);
  line-height: 1.5;

  @media (max-width: 680px) {
    flex-direction: column;
    gap: 35px;
  }

  > div {
    flex: 1 50%;
  }
`
const Button = styled(OutlineButton)`
  border: none;
  padding: 1em 3em;
  border-radius: 0;
  background-color: #24293C;
  color: #fff;
  transition: all 300ms;
  text-decoration: none;

  &:hover, &:focus {
    background-color: #fff;
    color: #24293C;
  }
`


const LearnMoreSection = styled.section`
  padding: 3rem 1rem;
  background-color: #252A3D;
  background-image: url("../images/reset/learn-more-bg.png");
  background-repeat: no-repeat;
  background-position: left top;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  h2 {
    color: #fff;
    font-size: clamp(1.875rem, -0.1103rem + 5.2941vw, 4.125rem);
    font-weight: 500;
    text-align: center;
    margin-bottom: 1rem;
  }
`

const LearnMoreCTA = styled(OutlineButton)`
  &[class] {
    border-color: #6A7792;
    background-color: #6A7792;
    border-radius: 0;
    color: #FFF;
    margin-bottom: 2rem !important;
    display: inline-block;
    font-size: 18px;
    font-weight: 500;
    padding: 0.5em 2em;
    text-transform: none;

    &:hover, &:focus {
      background-color: #DAE0EE;
      color: #222A3F;
    }
  }
`

const CTASection = styled.section`
  padding: 100px 0;
  background-color: #717C97;
  background-image: url("../images/reset/reset-single-cta-bg.png");
  background-repeat: no-repeat;
  background-size: auto 90%;
  background-position: bottom right;
`

const CTAInner = styled.div`
  max-width: 1060px;
  margin: 0 auto;
  padding: 0 1rem;

  .text-wrapper {
    max-width: 650px;
    text-wrap: balance;
    color: #fff;
    font-size: clamp(1rem, 0.7243rem + 0.7353vw, 1.3125rem);
    line-height: 1.5;
    margin-bottom: 35px;
  }

  h2 {
    color: #fff;
    font-size: clamp(1.625rem, 0.3015rem + 3.5294vw, 3.125rem);
  }
`

const FAQSection = styled.section`
  padding: 100px 0;
  background-color: #fff;
`

const FAQInner = styled.div`
  max-width: 1060px;
  margin: 0 auto;
  padding: 0 1rem;

  h2 {
    font-size: clamp(1.5rem, 1.0037rem + 1.3235vw, 2.0625rem);
    color: #6C7E99;
  }
`

const FAQs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 35px;
`

const Faq = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: solid 1px #CCD6ED;
  padding-bottom: 35px;
  font-size: clamp(1rem, 0.7243rem + 0.7353vw, 1.3125rem);

  button {
    background-color: transparent;
    border: none;
    padding: 0;
    text-align: left;
    cursor: pointer;
    -webkit-appearance: none;
    appearance: none;
    position: relative;

    &:after {
      content: "+";
      position: absolute;
      top: 0;
      right: 0;
      font-size: 25px;
      color: #8f9ab2
      transition: all 300ms;
    }

    &[aria-expanded="true"] {
      &:after {
        content: "-";
      }
    }
  }
  h3 {
    font-family: "Noto Sans", sans-serif;
    margin-bottom: 0;
    font-weight: 700;
    font-size: clamp(1rem, 0.7243rem + 0.7353vw, 1.3125rem);
  }

  > div {
    margin-top: 10px;
    visibility: hidden;
    opacity: 0;
    max-height: 0;
    line-height: 1.2;

    &[aria-hidden="false"] {
      visibility: visible;
      opacity: 1;
      max-height: 10000px;
      transition: all 300ms;
    }

    > *:last-child {
      margin-bottom: 0;
    }
  }
`

const Videos = styled.div`
  display: flex;
  gap: 35px;
  max-width: none;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    flex-direction: column;
  }

  > div {
    aspect-ratio: 16/9;
    box-shadow: 4px 7px 17px 0px rgba(0,0,0,0.75);
    width: 100%;
    line-height: 0;

    iframe {
      width: 100%;
      height: 100%;
      aspect-ratio: 16/9;
      margin-bottom: 0;
      line-height: 0;
    }
  }
`

const GeneralPriceStatement = styled.p`
  font-size: clamp(1.125rem, 0.9044rem + 0.5882vw, 1.375rem);
  line-height: 1.5;
  max-width: 925px;
  margin: 0 auto;
  padding-bottom: 1.5rem;
  text-align: center;
`;

const PriceRange = styled.p`
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 0;
  color: #222a3f;
`;

const PriceDisclaimer = styled.p`
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 1rem;
  color: #444;
`;

const Spacer = styled.div`
  height: 4rem;

  @media (max-width: 768px) {
    height: 2rem;
  }
`;

const NegativeSpacer = styled.div`
  height: 4rem;
  margin-top: -8rem;

  @media (max-width: 768px) {
    height: 2rem;
    margin-top: -4rem;
  }
`;
  

const ResetTemplate = data => {
  const page = data.page;
  const fields = page.resetTemplateFields;

  const templateLayout = fields.templateLayout || [];
  const layout = templateLayout[0] || 'default';

  const compareBeforeImage = getImage(fields.comparisonSectionBeforeImage?.node?.localFile)
  const compareAfterImage = getImage(fields.comparisonSectionAfterImage?.node?.localFile)

  const comparisonCharts = fields.comparisonCharts || [];

  const playVideo = (e) => {
    e.preventDefault();
    const parent = e.target.closest('[data-url]');
    const dataUrl = parent.getAttribute('data-url');
    const iframe = document.createElement('iframe');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay');

    if ( dataUrl.match(/youtube\.com/) ) {
      let youtubeID;

      if ( dataUrl.match(/(\?|&)v=/) ) {
        youtubeID = dataUrl.split('v=')[1];
      } else if ( dataUrl.match(/(\.be\/)+/) ) {
        youtubeID = dataUrl.split('.be/')[1];
      } else if ( dataUrl.match(/(shorts\/)+/) ) {
        youtubeID = dataUrl.split('shorts/')[1];
      }

      const queryPosition = youtubeID.indexOf('?');
      const ampersandPosition = youtubeID.indexOf('&');
      
      if (queryPosition !== -1) {
        youtubeID = youtubeID.substring(0, queryPosition);
      }

      if (ampersandPosition !== -1) {
        youtubeID = youtubeID.substring(0, ampersandPosition);
      }

      const url = `https://www.youtube.com/embed/${youtubeID}?autoplay=1&loop=1`;
      
      iframe.setAttribute('src', url);
      parent.innerHTML = iframe.outerHTML;
      iframe.addEventListener('load', () => {
        iframe.play();
      });
    } else if ( dataUrl.match(/vimeo\.com/) ) {
      let vimeoID = dataUrl.split('vimeo.com/')[1];

      const queryPosition = vimeoID.indexOf('?');
      const ampersandPosition = vimeoID.indexOf('&');
      
      if (queryPosition !== -1) {
        vimeoID = vimeoID.substring(0, queryPosition);
      }

      if (ampersandPosition !== -1) {
        vimeoID = vimeoID.substring(0, ampersandPosition);
      }

      const url = `https://player.vimeo.com/video/${vimeoID}?autoplay=1&loop=1`;
      iframe.setAttribute('src', url);
      parent.innerHTML = iframe.outerHTML;
      iframe.addEventListener('load', () => {
        iframe.play();
      });
    }
  }

  const toggleFAQ = (e) => {
    const button = e.currentTarget;
    const faq = button.closest("div");
    const div = faq.querySelector("div");
    const expanded = button.getAttribute("aria-expanded") === "true" || false;
    button.setAttribute("aria-expanded", !expanded);
    div.setAttribute("aria-hidden", expanded);
  }

  const ColumnsBlock = () => (
    <ColumnSection>
      {'layout2' === layout && <Spacer />}
      <ColumnInner>
        {fields.columnSectionTitle && <h2>{fields.columnSectionTitle}</h2>}
        <Columns>
          <div>
            {fields.columnSectionColumn1Text && parse(fields.columnSectionColumn1Text)}
          </div>
          <div>
            {fields.columnSectionColumn2Text && parse(fields.columnSectionColumn2Text)}
          </div>
        </Columns>
      </ColumnInner>
    </ColumnSection>
  )

  return (
    <Layout>
      <Seo title={page.seo.title} description={page.seo.metaDesc} seo={page.seo} />
      <style
        dangerouslySetInnerHTML={
          {
            __html: `
              .page-header:not(.sticky){
                box-shadow: none;
              }
              .page-header:not(.sticky),
              .page-header:not(.sticky) #header-menu-toggle {
                background-color: transparent;
              }

              @media (min-width: 1204px) {
                .page-header:not(.sticky) *:not(#header-menu-explore, #header-menu-explore-toggle, #search-toggle) {
                  background-color: transparent;
                }
              }
            `
          }
        }
      />
      <HeroSection>
        <HeroContainer>
          <h1>{page.title}</h1>
          {page.content && parse(page.content)}
          <HeroCompare>
            {fields.sideBySideImage && fields.sideBySideImage.map((item, index) => {
              const before = getImage(item.before?.node?.localFile);
              const after = getImage(item.after?.node?.localFile);

              return (
                <ReactCompareSlider
                  key={index}
                  style={{
                    height: `auto`,
                    objectFit: `contain`,
                    width: `100%`,
                  }}
                  itemOne={
                    <GatsbyImage
                      image={before}
                      alt={item.before?.node?.altText}
                      style={{
                        ...styleFitContainer({
                          objectFit: "contain",
                          objectPosition: "center",
                          height: `100%`,
                          width: `auto`,
                        }),
                      }}
                    />
                  }
                  itemTwo={
                    <GatsbyImage
                      image={after}
                      alt={item.after?.node?.altText}
                      style={{
                        ...styleFitContainer({
                          objectFit: "contain",
                          objectPosition: "center",
                          height: `100%`,
                          width: `auto`,
                        }),
                      }}
                    />
                  }
                />
              );
            })}
          </HeroCompare>
        </HeroContainer>
      </HeroSection>
      <CompareSection>
      {fields.generalPriceStatement && <GeneralPriceStatement>{parse(fields.generalPriceStatement)}</GeneralPriceStatement>}
        <CompareInner>
          <div>
            <h2>{fields.comparisonSectionTitle}</h2>
            {fields.treatmentPriceRange && <PriceRange>{fields.treatmentPriceRange}</PriceRange>}
            {fields.treatmentPriceRange && fields.treatmentPriceDisclaimer && <PriceDisclaimer>{fields.treatmentPriceDisclaimer}</PriceDisclaimer>}
            {fields.comparisonSectionContent && parse(fields.comparisonSectionContent)}
            <Button to={fields.comparisonSectionButton.comparisonSectionButtonUrl}>{fields.comparisonSectionButton.comparisonSectionButtonText}</Button>
          </div>
          <div>
            {fields.comparisonSectionBeforeImage && fields.comparisonSectionAfterImage &&
            <ReactCompareSlider
              style={{
                height: `auto`,
                objectFit: `contain`,
                width: `100%`,
              }}
              itemOne={
                <GatsbyImage
                  image={compareBeforeImage}
                  alt={fields.comparisonSectionBeforeImage.node.altText}
                  style={{
                    ...styleFitContainer({
                      objectFit: "contain",
                      objectPosition: "center",
                      height: `100%`,
                      width: `auto`,
                    }),
                  }}
                />
              }
              itemTwo={
                <GatsbyImage
                  image={compareAfterImage}
                  alt={fields.comparisonSectionAfterImage.node.altText}
                  style={{
                    ...styleFitContainer({
                      objectFit: "contain",
                      objectPosition: "center",
                      height: `100%`,
                      width: `auto`,
                    }),
                  }}
                />
              }
            />
            }
            {
              fields.comparisonSectionVideo.heading &&
              <h3 className="video-intro">{fields.comparisonSectionVideo.heading}</h3>
            }
            {
              fields.comparisonSectionVideo.embedUrl &&
              <YouTubeWrapper data-url={fields.comparisonSectionVideo.embedUrl}>
                {fields.comparisonSectionVideo.poster && <GatsbyImage image={fields.comparisonSectionVideo.poster.node.localFile.childImageSharp.gatsbyImageData} alt={fields.comparisonSectionVideo.poster.node.altText || ''} /> }
                <button aria-label="Play" onClick={playVideo}><span></span></button>
              </YouTubeWrapper>
            }
          </div>
        </CompareInner>
      </CompareSection>
      {'layout2' === layout &&
        <>
          <ColumnsBlock />
        </>
      }
      {comparisonCharts && <>
        {comparisonCharts.map(
          (chart, index) => {
            return (
              <ComparisonCharts key={index} {...chart} />
            )
          }
        )}
      </>}
      {fields.gridSectionFields && 
      <GridSection>
        <GridInner>
        {'layout2' === layout && fields.gridSectionText && 
          <>
            <NegativeSpacer />
            {parse(fields.gridSectionText)}
            <Spacer />
          </>
        }
          <h2>{fields.gridSectionTitle}</h2>
          <Grid>
            {
              fields.gridSectionFields.map((item, index) => {
                const image = item.itemImage && item.itemImage.node.localFile ? getImage(item.itemImage.node.localFile) : null;
                return (
                  <div key={index}>
                    {image && <GatsbyImage image={image} alt="" />}
                    <div>
                      {item.itemLabel}
                    </div>
                  </div>
                )
              })
            }
          </Grid>
          {'layout2' !== layout && fields.gridSectionText && parse(fields.gridSectionText)}
        </GridInner>
      </GridSection>
    }
      {'layout2' !== layout &&
        <ColumnsBlock />
      }
      {
        (! fields.ctaVideos || fields.ctaVideos.length < 1) &&
        (fields.ctaTitle || (fields.ctaButton.ctaButtonUrl && fields.ctaButton.ctaButtonText)) &&
        <LearnMoreSection>
          {fields.ctaTitle && <h2>{fields.ctaTitle}</h2>}
          {fields.ctaButton.ctaButtonUrl && fields.ctaButton.ctaButtonText && 
            <LearnMoreCTA to={fields.ctaButton.ctaButtonUrl}>{fields.ctaButton.ctaButtonText}</LearnMoreCTA>
          }
        </LearnMoreSection>
      }
      {fields.ctaVideos && fields.ctaVideos.length > 0 &&
      <CTASection>
        <CTAInner>
          <h2>{fields.ctaTitle && fields.ctaTitle}</h2>
          {fields.ctaText && <div className="text-wrapper">
            {fields.ctaText && parse(fields.ctaText)}
          </div>}
          {fields.ctaVideos && fields.ctaVideos.length > 0 && 
            <Videos>
              {fields.ctaVideos && fields.ctaVideos.length > 0 && fields.ctaVideos.map((item, index) => {
                /* eslint-disable-next-line */
                const youtubeID = item.youtubeUrl ? item.youtubeUrl.replace(/.*\/\/(?:www.youtube.com\/watch\?.*v=([^&\r\n]+)|youtu.be\/([^\?\/]+).+)/, '$1$2') : '';
                const compare = item.compare ? item.compare : false;
                const before = compare ? getImage(compare.before?.node?.localFile) : null;
                const after = compare ? getImage(compare.after?.node?.localFile) : null;
                return (
                  <div key={index}>
                    {youtubeID &&
                      <iframe
                        title={`Video ${index + 1}`}
                        width="100%"
                        src={`https://www.youtube.com/embed/${youtubeID}`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    }
                    {!youtubeID && compare &&
                      <ReactCompareSlider
                        style={{
                          height: `auto`,
                          objectFit: `contain`,
                          width: `100%`,
                        }}
                        itemOne={
                          <GatsbyImage
                            image={before}
                            alt={compare.before?.node.altText}
                            style={{
                              ...styleFitContainer({
                                objectFit: "contain",
                                objectPosition: "center",
                                height: `100%`,
                                width: `auto`,
                              }),
                            }}
                          />
                        }
                        itemTwo={
                          <GatsbyImage
                            image={after}
                            alt={compare.after?.node.altText}
                            style={{
                              ...styleFitContainer({
                                objectFit: "contain",
                                objectPosition: "center",
                                height: `100%`,
                                width: `auto`,
                              }),
                            }}
                          />
                        }
                      />
                    }
                  </div>
                )
              })}
            </Videos>
          }
          {fields.ctaButton.ctaButtonUrl && fields.ctaButton.ctaButtonText && 
            <Button to={fields.ctaButton.ctaButtonUrl}>{fields.ctaButton.ctaButtonText}</Button>
          }
        </CTAInner>
      </CTASection>
      }
      {fields.faqItems && <FAQSection>
        <FAQInner>
          <h2>{fields.faqSectionTitle}</h2>
          <FAQs>
            {
              fields.faqItems.map((item, index) => {
                return (
                  <Faq key={index}>
                    <button onClick={toggleFAQ} aria-expanded="false">
                      <h3>{item.itemTitle || ''}</h3>
                    </button>
                    <div aria-hidden="true">
                      {item.itemContent && parse(item.itemContent)}
                    </div>
                  </Faq>
                )
              })
            }
          </FAQs>
        </FAQInner>
      </FAQSection>
      }
    </Layout>
  )
}

export default ResetTemplate
